import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth.service';
import { EditConfigService } from './edit-config.modal.service';

@Component({
  selector: 'app-edit-config',
  templateUrl: './edit-config.modal.component.html',
  styleUrls: ['./edit-config.modal.component.scss'],
})
export class EditConfigModalComponent implements OnInit {
  @ViewChild('requestTOTPCode') requestTOTPCode!: TemplateRef<any>;

  private _account: any;
  private totpModalRef?: NgbModalRef;

  @Input() set account(value: any) {
    this._account = value;
    if (value) {
      this.patchInitialValues();
    }
  }

  get account(): any {
    return this._account;
  }

  public isSaving = false;
  public validatingTotp = false;
  public errorMessage = '';
  public validationCode = '';

  configForm = new FormGroup(
    {
      // LIMITES FINANCEIROS
      valorMinimoSaqueAutomatico: new FormControl<number>(0, { nonNullable: true }),
      amountExpired: new FormControl<number>(0, { nonNullable: true }),
      valueExpired: new FormControl<number>(0, { nonNullable: true }),
      analyzeWithdrawal: new FormControl<boolean>(false, { nonNullable: true }),
      valueMinRequest: new FormControl<number>(0, { nonNullable: true }),
      valueMaxRequest: new FormControl<number>(0, { nonNullable: true }),
      depositValueMinRequest: new FormControl<number>(0, { nonNullable: true }),
      depositValueMaxRequest: new FormControl<number>(0, { nonNullable: true }),

      // PERMISSÕES
      featureFlagRefundPix: new FormControl<boolean>(false, { nonNullable: true }),
      featureFlagCpfWithdraw: new FormControl<boolean>(false, { nonNullable: true }),
    },
    { validators: [this.configRangesValidator] }
  );

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private authService: AuthService,
    private editConfigService: EditConfigService
  ) {}

  ngOnInit(): void {
    this.patchInitialValues();
  }

  private patchInitialValues(): void {
    const src = this.account ?? {};
    const withdraw = src.withDrawConfiguration ?? {};
    const deposit = src.depositConfiguration ?? {};

    this.configForm.patchValue({
      valorMinimoSaqueAutomatico: this.toNumber(
        src.valorMinimoSaqueAutomatico ?? withdraw.valorMinimoSaqueAutomatico
      ),
      amountExpired: this.toNumber(
        src.amountExpired ?? withdraw.amountExpired
      ),
      valueExpired: this.toNumber(
        src.valueExpired ?? withdraw.valueExpired
      ),
      analyzeWithdrawal: !!(
        src.analyzeWithdrawal ?? withdraw.analyzeWithdrawal
      ),
      valueMinRequest: this.toNumber(
        src.valueMinRequest ?? withdraw.valueMinRequest
      ),
      valueMaxRequest: this.toNumber(
        src.valueMaxRequest ?? withdraw.valueMaxRequest
      ),

      depositValueMinRequest: this.toNumber(
        src.depositValueMinRequest ?? deposit.valueMinRequest
      ),
      depositValueMaxRequest: this.toNumber(
        src.depositValueMaxRequest ?? deposit.valueMaxRequest
      ),

      featureFlagRefundPix: !!src.featureFlagRefundPix,
      featureFlagCpfWithdraw: !!src.featureFlagCpfWithdraw,
    });
  }

  private toNumber(v: any): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }

  close(): void {
    this.activeModal.close();
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  openTotpModal(): void {
    this.errorMessage = '';
    this.validationCode = '';

    this.totpModalRef = this.modalService.open(this.requestTOTPCode, {
      backdrop: 'static',
      keyboard: true,
      centered: true,
      size: 'sm',
    });
  }

  async pasteTotpCode(): Promise<void> {
    try {
      this.validationCode = (await navigator.clipboard.readText()).trim();
    } catch (err) {
      console.error('Erro ao colar token:', err);
    }
  }

  async isValidTotp(): Promise<boolean> {
    try {
      const res = await this.authService.validateTotpCode(this.validationCode);
      return !!res?.isValid;
    } catch {
      return false;
    }
  }

  save(): void {
    this.configForm.markAllAsTouched();

    if (this.configForm.invalid) {
      this.toastr.warning('Corrija os campos inválidos antes de salvar.', 'Aviso', {
        timeOut: 2500,
        positionClass: 'toast-top-right',
      });
      return;
    }

    this.openTotpModal();
  }

  async validateTotp(): Promise<void> {
    if (this.validatingTotp || this.isSaving) return;

    this.validatingTotp = true;
    this.errorMessage = '';

    const ok = await this.isValidTotp();
    if (!ok) {
      this.errorMessage = 'Código de autenticação inválido!';
      this.validatingTotp = false;
      return;
    }

    const payload = this.buildPayload();
    this.isSaving = true;

    this.editConfigService
      .updateAccountAdm(payload)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.validatingTotp = false;
        })
      )
      .subscribe({
        next: () => {
          this.totpModalRef?.close();
          this.toastr.success('Configurações atualizadas com sucesso!', 'Sucesso', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });

          this.activeModal.close('saved');
        },
        error: (err) => {
          console.error(err);
          this.toastr.error('Erro ao atualizar configurações.', 'Erro', {
            timeOut: 3000,
            positionClass: 'toast-top-right',
          });
        },
      });
  }

  private buildPayload(): any {
    const v = this.configForm.getRawValue();

    return {
      valorMinimoSaqueAutomatico: this.toNumber(v.valorMinimoSaqueAutomatico),
      amountExpired: this.toNumber(v.amountExpired),
      valueExpired: this.toNumber(v.valueExpired),
      analyzeWithdrawal: !!v.analyzeWithdrawal,
      valueMinRequest: this.toNumber(v.valueMinRequest),
      valueMaxRequest: this.toNumber(v.valueMaxRequest),
      depositValueMinRequest: this.toNumber(v.depositValueMinRequest),
      depositValueMaxRequest: this.toNumber(v.depositValueMaxRequest),

      featureFlagRefundPix: !!v.featureFlagRefundPix,
      featureFlagCpfWithdraw: !!v.featureFlagCpfWithdraw,
    };
  }

  configRangesValidator(control: AbstractControl): ValidationErrors | null {
    const getNum = (name: string): number => {
      const raw = control.get(name)?.value;
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    };

    const errors: ValidationErrors = {};

    const withdrawMin = getNum('valueMinRequest');
    const withdrawMax = getNum('valueMaxRequest');
    if (withdrawMax < withdrawMin) {
      errors['withdrawRangeInvalid'] = true;
    }

    const depositMin = getNum('depositValueMinRequest');
    const depositMax = getNum('depositValueMaxRequest');
    if (depositMax < depositMin) {
      errors['depositRangeInvalid'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  hasRangeError(type: 'withdraw' | 'deposit'): boolean {
    const errors = this.configForm.errors ?? {};
    return type === 'withdraw'
      ? !!errors['withdrawRangeInvalid']
      : !!errors['depositRangeInvalid'];
  }
}